import { useEffect, useMemo, useSyncExternalStore } from "react";
import type {
	FreeArea,
	HostToClientMessage,
	MessagePayload,
	OrientationType,
	PlatformType,
} from "@rubyplay/effw-connector";
import type { EngagementProviderBootInput } from "@/provider/ProviderTypes";
import { resolveFreeAreas } from "@/host/resolveFreeAreas";
import { useCommunicator } from "@/contexts/CommunicatorContext";
import {
	fetchEngagementInitOrThrow,
	resolveCaptainUpInitEndpoint,
} from "@/integrations/captainup/CaptainUpAuthService";
import {
	buildHostProviderBootInput,
	isHostConfigured,
	isHostGameReady,
	isProviderBootEligible,
	type HostSessionConfig,
	type HostSessionSnapshot,
} from "@/host/HostSessionTypes";
import { getHostSessionStore } from "@/host/HostSessionStore";
import { debugLog, debugWarn } from "@/utils";
import { useSubscription } from "@/hooks/useSubscription";

export type HostSessionState = {
	hostTransportReady: boolean;
	hostConfigured: boolean;
	hostStartupPreloadRequested: boolean;
	hostGameReady: boolean;
	providerBootEligible: boolean;
	providerBootInput: EngagementProviderBootInput | null;
	cheatsEnabled: boolean;
};

function normalizeIntegrationType(rawIntegrationType: unknown): string | undefined {
	if (typeof rawIntegrationType !== "string") {
		return undefined;
	}

	const normalized = rawIntegrationType.trim().toLowerCase();

	if (!normalized) {
		return undefined;
	}

	return normalized;
}

function toHostPlatform(rawPlatform: unknown): PlatformType {
	return rawPlatform === "mobile" ? "mobile" : "desktop";
}

function toHostOrientation(rawOrientation: unknown): OrientationType {
	return rawOrientation === "portrait" ? "portrait" : "landscape";
}

function toIsCheatsEnabled(
	payload: MessagePayload<HostToClientMessage, "ConfigConnection">
): boolean {
	const payloadWithCheats = payload as typeof payload & {
		isCheatsEnabled?: unknown;
	};

	return payloadWithCheats.isCheatsEnabled === true;
}

function toHostSessionConfig(
	payload: MessagePayload<HostToClientMessage, "ConfigConnection">
): HostSessionConfig {
	return {
		sessionKey: String(payload.sessionKey ?? "").trim(),
		serverUrl: String(payload.serverUrl ?? "").trim(),
		gameName: String(payload.gameName ?? "").trim(),
		lang: String(payload.lang ?? "").trim(),
		currency: String(payload.currency ?? "").trim(),
		operator: String(payload.operator ?? "").trim(),
		platform: toHostPlatform(payload.platform),
		orientation: toHostOrientation(payload.orientation),
		integrationType: normalizeIntegrationType(payload.integrationType),
		isHomeButtonEnabled: Boolean(payload.isHomeButtonEnabled),
		isCheatsEnabled: toIsCheatsEnabled(payload),

		bet: typeof payload.bet === "number" ? payload.bet : undefined,
		subunit:
			typeof payload.subunit === "number" ? payload.subunit : undefined,
		funMode:
			typeof payload.funMode === "boolean" ? payload.funMode : undefined,
		funModeBalance:
			typeof payload.funModeBalance === "number"
				? payload.funModeBalance
				: undefined,
		regulationType:
			typeof payload.regulationType === "string"
				? payload.regulationType
				: undefined,
		iframeContainerId:
			typeof payload.iframeContainerId === "string"
				? payload.iframeContainerId
				: undefined,
	};
}

function toEngagementProviderBootInput(
	snapshot: HostSessionSnapshot
): EngagementProviderBootInput | null {
	const hostBootInput = buildHostProviderBootInput(snapshot);

	if (!hostBootInput) {
		return null;
	}

	return {
		sessionKey: hostBootInput.sessionKey,
		serverUrl: hostBootInput.serverUrl,
		gameName: hostBootInput.gameName,
		lang: hostBootInput.lang,
		operator: hostBootInput.operator,
		platform: hostBootInput.platform,
		orientation: hostBootInput.orientation,
		integrationType: hostBootInput.integrationType,
	};
}

async function resolveInitialFreeAreas(
	payload: MessagePayload<HostToClientMessage, "ConfigConnection">
): Promise<FreeArea[]> {
	const isHomeButtonEnabled = Boolean(payload.isHomeButtonEnabled);

	try {
		const endpointUrl = resolveCaptainUpInitEndpoint(payload.serverUrl);
		const response = await fetchEngagementInitOrThrow(payload.sessionKey, {
			mode: "network",
			endpointUrl,
		});

		return resolveFreeAreas(response.widget, isHomeButtonEnabled);
	} catch (error) {
		const fallbackAreas = resolveFreeAreas(undefined, isHomeButtonEnabled);

		debugWarn("[EF HostSession] failed to resolve widget config from /engagement/init; using default free areas", {
			gameName: payload.gameName,
			error: error instanceof Error ? error.message : String(error),
			freeAreasCount: fallbackAreas.length,
		});

		return fallbackAreas;
	}
}

export function useHostSession(): HostSessionState {
	const {
		onHostError,
		onConfigConnection,
		onStartupPreload,
		onGameReady,
		ackConfigConnection,
		onOrientationChange,
		onUpdateFreeAreas,
		isStandalone,
	} = useCommunicator();

	const standalone = isStandalone();
	const hostSessionStore = getHostSessionStore();

	const snapshot = useSyncExternalStore(
		(onStoreChange) => hostSessionStore.subscribe(() => onStoreChange()),
		() => hostSessionStore.getSnapshot(),
		() => hostSessionStore.getSnapshot()
	);

	useEffect(() => {
		if (standalone) {
			hostSessionStore.dispatch({ type: "TRANSPORT_READY" });
		}
	}, [hostSessionStore, standalone]);

	useSubscription(
		onStartupPreload,
		(_payload: MessagePayload<HostToClientMessage, "StartupPreload">) => {
			debugLog("[EF HostSession] host -> StartupPreload", {
				phaseBefore: snapshot.phase,
				configuredBefore: snapshot.configured,
				startupPreloadRequestedBefore: snapshot.startupPreloadRequested,
			});

			hostSessionStore.dispatch({
				type: "STARTUP_PRELOAD_RECEIVED",
			});
		},
		[
			hostSessionStore,
			snapshot.configured,
			snapshot.phase,
			snapshot.startupPreloadRequested,
		]
	);

	useSubscription(
		onGameReady,
		(_payload: MessagePayload<HostToClientMessage, "GameReady">) => {
			debugLog("[EF HostSession] host -> GameReady", {
				phaseBefore: snapshot.phase,
				transportReadyBefore: snapshot.transportReady,
				configuredBefore: snapshot.configured,
				gameReadyBefore: snapshot.gameReady,
			});

			hostSessionStore.dispatch({
				type: "GAME_READY_RECEIVED",
			});
		},
		[
			hostSessionStore,
			snapshot.configured,
			snapshot.gameReady,
			snapshot.phase,
			snapshot.transportReady,
		]
	);

	useSubscription(
		onHostError,
		(_payload: MessagePayload<HostToClientMessage, "Error">) => {
			// debugError("[EF HostSession] host -> Error", payload);
		},
		[]
	);

	useSubscription(
		onConfigConnection,
		(payload: MessagePayload<HostToClientMessage, "ConfigConnection">) => {
			const nextConfig = toHostSessionConfig(payload);

			debugLog("[EF HostSession] host -> ConfigConnection", {
				gameName: payload.gameName,
				platform: payload.platform,
				orientation: payload.orientation,
				operator: payload.operator,
				integrationType: payload.integrationType,
				isHomeButtonEnabled: payload.isHomeButtonEnabled,
				isCheatsEnabled: nextConfig.isCheatsEnabled,
				sessionKeyPresent: !!payload.sessionKey,
				phaseBefore: snapshot.phase,
				transportReadyBefore: snapshot.transportReady,
				configuredBefore: snapshot.configured,
				gameReadyBefore: snapshot.gameReady,
			});

			void (async () => {
				const initialAreas = await resolveInitialFreeAreas(payload);

				hostSessionStore.dispatch({
					type: "CONFIG_RECEIVED",
					payload: {
						config: nextConfig,
						freeAreas: initialAreas,
					},
				});

				ackConfigConnection({
					success: true,
					freeAreas: initialAreas,
					assetsToLoadCount: 0,
					isMultiFreeZone: true,
				});

				debugLog("[EF HostSession] ConfigConnectionAck sent", {
					success: true,
					platform: nextConfig.platform,
					orientation: nextConfig.orientation,
					freeAreasCount: initialAreas.length,
					phaseAfter: hostSessionStore.getSnapshot().phase,
					transportReadyAfter: hostSessionStore.getSnapshot().transportReady,
					configuredAfter: hostSessionStore.getSnapshot().configured,
				});
			})();
		},
		[
			ackConfigConnection,
			hostSessionStore,
			snapshot.configured,
			snapshot.gameReady,
			snapshot.phase,
			snapshot.transportReady,
		]
	);

	useSubscription(
		onOrientationChange,
		(payload: { orientation: OrientationType }) => {
			debugLog("[EF HostSession] host -> Orientation", payload);

			hostSessionStore.dispatch({
				type: "ORIENTATION_UPDATED",
				payload: {
					orientation:
						payload.orientation === "portrait"
							? "portrait"
							: "landscape",
				},
			});
		},
		[hostSessionStore]
	);

	useSubscription(
		onUpdateFreeAreas,
		(payload: MessagePayload<HostToClientMessage, "UpdateFreeAreas">) => {
			const next = payload.freeAreas;

			debugLog("[EF HostSession] host -> UpdateFreeAreas", {
				count: next.length,
				areaIds: next.map((area) => area.id),
			});

			hostSessionStore.dispatch({
				type: "FREE_AREAS_UPDATED",
				payload: {
					freeAreas: next,
				},
			});
		},
		[hostSessionStore]
	);

	const derivedState = useMemo<HostSessionState>(() => {
		const hostTransportReady = standalone || snapshot.transportReady;
		const hostConfigured = standalone || isHostConfigured(snapshot);
		const hostStartupPreloadRequested = standalone || snapshot.startupPreloadRequested;
		const hostGameReady = standalone || isHostGameReady(snapshot);
		const providerBootEligible = isProviderBootEligible(snapshot);
		const providerBootInput = toEngagementProviderBootInput(snapshot);
		const cheatsEnabled = snapshot.config?.isCheatsEnabled === true;

		return {
			hostTransportReady,
			hostConfigured,
			hostStartupPreloadRequested,
			hostGameReady,
			providerBootEligible,
			providerBootInput,
			cheatsEnabled,
		};
	}, [snapshot, standalone]);

	useEffect(() => {
		debugLog("[EF HostSession] derived state snapshot", {
			standalone,
			phase: snapshot.phase,
			hostTransportReady: derivedState.hostTransportReady,
			hostConfigured: derivedState.hostConfigured,
			hostStartupPreloadRequested: derivedState.hostStartupPreloadRequested,
			hostGameReady: derivedState.hostGameReady,
			providerBootEligible: derivedState.providerBootEligible,
			hasProviderBootInput: !!derivedState.providerBootInput,
			cheatsEnabled: derivedState.cheatsEnabled,
			lastUpdatedAtMs: snapshot.lastUpdatedAtMs,
		});
	}, [derivedState, snapshot, standalone]);

	return derivedState;
}
